/**
 * Config-mode keyboard toggle.
 *
 * Mounted **once** at the app root; renders nothing. The component's
 * only job is to attach a document-level `keydown` listener that
 * toggles config mode on `CMD`/`Ctrl` + `Shift` + `E`.
 *
 * The listener is attached unconditionally for every mounted user —
 * toggling config mode is a cheap boolean flip, and the real access
 * check lives on the `/admin/*` routes, which `AdminAuthGuard` guards.
 * A non-admin who toggles config mode and clicks the resulting
 * Configure button lands on the admin login page.
 *
 * The earlier click-capture flow (CMD/Ctrl+click on a registered
 * component to navigate to its admin config page) was removed in
 * favor of an explicit on-component {@link ./ConfigureButton.ConfigureButton}
 * affordance rendered by `withComponentRules`. The exported symbol
 * keeps the `ConfigClickHandler` name for API stability.
 */

import { useEffect } from 'react';

import { useConfigMode } from './configModeStore';
import { isToggleShortcut } from './configClickHandlerUtils';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Config-mode keyboard toggle handler.
 *
 * Mount once at the application root. Renders nothing; attaches a single
 * document-level `keydown` listener that toggles config mode via
 * `CMD`/`Ctrl` + `Shift` + `E`.
 *
 * @returns `null` — the handler renders no DOM.
 */
export function ConfigClickHandler(): null {
  const { toggle } = useConfigMode();

  // ---------------------------------------------------------------------
  // Keyboard shortcut — active for every mounted user. The shortcut is
  // the *toggle*, so gating on `enabled` would make it impossible to
  // turn the mode on in the first place. Admin-only access to the
  // linked config page is enforced by `AdminAuthGuard` on `/admin/*`.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isToggleShortcut(event)) {
        return;
      }
      event.preventDefault();
      toggle();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggle]);

  return null;
}
